import { Location } from "react-router-dom";
import Workspace, { WorkspaceFolder } from "@/models/workspace/Workspace";

/**
 * Type for path items used in folder navigation
 */
export type PathItem = { id: string; name: string };

/**
 * Finds a folder by ID in the workspace hierarchy
 * @param workspace - The workspace to search in
 * @param folderId - The ID of the folder to find
 * @returns The found folder or null if not found
 */
export const findFolderById = (
  workspace: Workspace,
  folderId?: string
): WorkspaceFolder | null => {
  if (!folderId) return null;
  for (const folder of workspace.folders) {
    if (folder.id === folderId) return folder;

    const subFolder = findFolderInSubfolders(folder, folderId);
    if (subFolder) return subFolder;
  }
  return null;
};

/**
 * Collects all existing folder paths in the workspace
 * @param folders - The folders to collect paths from
 * @param basePath - The base path to start from
 * @returns A set of all existing folder paths
 */
export const collectExistingFolderPaths = (
  folders: WorkspaceFolder[],
  basePath: string = ""
): Set<string> => {
  const paths = new Set<string>();

  folders.forEach((f) => {
    const folderPath = basePath ? `${basePath}/${f.name}` : f.name;
    paths.add(folderPath);

    // Recursively collect paths from subfolders
    if (f.folders && f.folders.length > 0) {
      const subPaths = collectExistingFolderPaths(f.folders, folderPath);
      subPaths.forEach((path) => paths.add(path));
    }
  });

  return paths;
};

/**
 * Recursively searches for a folder in subfolders
 * @param parentFolder - The parent folder to search in
 * @param folderId - The ID of the folder to find
 * @returns The found folder or null if not found
 */
export const findFolderInSubfolders = (
  parentFolder: WorkspaceFolder,
  folderId: string
): WorkspaceFolder | null => {
  for (const folder of parentFolder.folders) {
    if (folder.id === folderId) return folder;

    const subFolder = findFolderInSubfolders(folder, folderId);
    if (subFolder) return subFolder;
  }
  return null;
};

/**
 * Reads the workspace id out of a `/workspaces/<id>/...` pathname.
 *
 * Used to pin a SharePoint download to the workspace it was started from: the
 * providers that own the download live above the router, so the route can
 * change while files are still being fetched.
 */
export const getWorkspaceIdFromPathname = (pathname: string): string | null =>
  pathname.match(/\/workspaces\/([^/]+)/)?.[1] ?? null;

/**
 * Extracts folder path from a `/workspaces/<id>/files/...` pathname
 * @param pathname - Route pathname, live or captured earlier
 * @returns Array of folder names from the URL path
 */
export const getFolderPathFromPathname = (pathname: string): string[] => {
  const pathSegments = pathname.split("/");
  const filesIndex = pathSegments.indexOf("files");
  if (filesIndex !== -1 && filesIndex + 1 < pathSegments.length) {
    return pathSegments
      .slice(filesIndex + 1)
      .map((segment) => decodeURIComponent(segment));
  }
  return [];
};

/**
 * Extracts folder path from URL
 * @param location - React Router location object
 * @returns Array of folder names from the URL path
 */
export const getFolderPathFromUrl = (location: Location): string[] =>
  getFolderPathFromPathname(location.pathname);

/**
 * Builds path array from folder names by traversing the workspace structure
 * @param workspace - The workspace to search in
 * @param folderNames - Array of folder names to build path from
 * @returns Array of PathItem objects representing the folder path
 */
export const buildPathFromNames = (
  workspace: Workspace,
  folderNames: string[]
): PathItem[] => {
  const path: PathItem[] = [];
  let currentFolders = workspace.folders;

  for (const folderName of folderNames) {
    const folder = currentFolders.find((f) => f.name === folderName);
    if (folder) {
      path.push({ id: folder.id, name: folder.name });
      currentFolders = folder.folders;
    } else {
      break; // Invalid path, stop here
    }
  }

  return path;
};

/**
 * Finds the path to a folder by its ID
 * @param folders - Array of folders to search in
 * @param targetId - The ID of the target folder
 * @param path - Current path being built (used in recursion)
 * @returns The path to the folder or null if not found
 */
export const findPathToFolder = (
  folders: WorkspaceFolder[],
  targetId: string,
  path: PathItem[] = []
): PathItem[] | null => {
  for (const folder of folders) {
    if (folder.id === targetId) {
      return [...path, { id: folder.id, name: folder.name }];
    }
    const subPath = findPathToFolder(folder.folders, targetId, [
      ...path,
      { id: folder.id, name: folder.name },
    ]);
    if (subPath) return subPath;
  }
  return null;
};

/**
 * Returns the folder path (e.g. "Kontrakter/Underprojekter") for a document
 * by searching the workspace folder hierarchy. Returns an empty string if the
 * document lives at the workspace root.
 */
export const getDocumentFolderPath = (
  workspace: Workspace,
  documentId: string
): string => {
  const search = (
    folders: WorkspaceFolder[],
    currentPath: string
  ): string | null => {
    for (const folder of folders) {
      const folderPath = currentPath
        ? `${currentPath}/${folder.name}`
        : folder.name;
      if (folder.documents.some((d) => d.id === documentId)) return folderPath;
      const nested = search(folder.folders, folderPath);
      if (nested !== null) return nested;
    }
    return null;
  };

  if (workspace.documents.some((d) => d.id === documentId)) return "";
  return search(workspace.folders, "") ?? "";
};

/**
 * Finds a folder ID by its path string (e.g., "folder1/subfolder1")
 * @param workspace - The workspace to search in
 * @param pathString - The folder path string to find
 * @param parentFolderId - Optional parent folder ID to start search from
 * @returns The folder ID if found, undefined otherwise
 */
export const findFolderIdByPath = (
  workspace: Workspace,
  pathString: string,
  parentFolderId?: string
): string | undefined => {
  if (!pathString) return undefined;

  const pathParts = pathString.split("/").filter(Boolean);
  if (pathParts.length === 0) return undefined;

  // Start from parent folder if provided, otherwise start from workspace root
  let currentFolders: WorkspaceFolder[] = parentFolderId
    ? findFolderById(workspace, parentFolderId)?.folders || []
    : workspace.folders;

  let foundFolderId: string | undefined = undefined;

  // Traverse the path
  for (const folderName of pathParts) {
    const folder = currentFolders.find(
      (f) => f.name.toLowerCase() === folderName.toLowerCase()
    );
    if (!folder) {
      return undefined; // Path not found
    }
    foundFolderId = folder.id;
    currentFolders = folder.folders;
  }

  return foundFolderId;
};
